import { useCallback, useMemo, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  fetchPostState,
  GetCompanySearchResponse,
  GetDirectorSearchResponse,
} from '@iwoca/lapi-client/edge';
import { fetchPostApiIntroducerPortalIntroducerCustomerApplication } from '@iwoca/lapi-client/introducer_portal';
import {
  Button,
  Card,
  CheckBox,
  Input,
  Label,
  Message,
  Tooltip,
} from '@iwoca/orion';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { useAmplitude } from 'hooks/useAmplitude';
import { TSupportedProductTypes } from 'lendingApi/applications.types';
import { TPostStateRequestBody } from 'lendingApi/lapiOverrides.types';
import { useForm } from 'react-hook-form';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { v4 } from 'uuid';
import { denormaliseDate } from '../helpers/stateNormalisation';
import { CompanyInputUK } from './CompanyInputUK/CompanyInputUK';
import { CriteriaItem } from './CriteriaItem/CriteriaItem';
import { DirectorSearch } from './DirectorSearch/DirectorSearch';
import styles from './NewSignupForm.module.css';
import { schema } from './NewSignUpForm.schema';

const ALLOWED_PRODUCTS: TSupportedProductTypes[] = [
  'credit_facility',
  'flexi24',
];

type TApplicationRequestBody = {
  data: {
    introducer: { introducer_id: string };
    requested_products: {
      product_type?: TSupportedProductTypes;
    }[];
  };
};

type TNewSignupFormData = {
  dobDay?: string;
  dobMonth?: string;
  dobYear?: string;
  privacyPolicyAgreement: boolean;
};

export const NewSignupForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCompany, setSelectedCompany] =
    useState<GetCompanySearchResponse['data'][number]>();
  const [selectedPerson, setSelectedPerson] =
    useState<GetDirectorSearchResponse['data'][number]>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { introducerId } = useIntroducerContext();
  const { trackEvent } = useAmplitude();

  const selectedProduct = searchParams.get(
    'selected_product',
  ) as TSupportedProductTypes;

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<TNewSignupFormData>({
    resolver: yupResolver(schema),
  });

  const setSelectedPersonWithDOB = (
    person: GetDirectorSearchResponse['data'][number] | undefined,
  ) => {
    setSelectedPerson(person);
    setValue('dobMonth', person?.month_of_birth?.toString());
    setValue('dobYear', person?.year_of_birth?.toString());
  };

  const setCompanyWithTracking = (
    company: GetCompanySearchResponse['data'][number],
  ) => {
    trackEvent(AMPLITUDE_EVENTS.companySearchSelected, {
      'Company name': company?.name,
      'Company number': company?.number,
      'Company type': company?.company_type,
    });
    setSelectedCompany(company);
  };

  const handleDOBTracking = () => {
    trackEvent(AMPLITUDE_EVENTS.dobUpdated);
  };

  const passesTypeCheck = useCallback(() => {
    if (!selectedCompany) {
      return false;
    }
    return !selectedCompany.excluded;
  }, [selectedCompany]);

  const passesDirectorCheck = useCallback(() => {
    if (!selectedPerson) {
      return false;
    }

    if (selectedPerson.roles?.includes('director')) {
      return true;
    }
  }, [selectedPerson]);

  const isSubmitDisabled = useMemo(() => {
    return (
      !selectedCompany ||
      !selectedPerson ||
      !passesTypeCheck() ||
      !passesDirectorCheck()
    );
  }, [passesDirectorCheck, passesTypeCheck, selectedCompany, selectedPerson]);

  const onSubmit = async (values: TNewSignupFormData) => {
    if (!selectedCompany || !selectedPerson || !introducerId) {
      return;
    }

    const state: TPostStateRequestBody = {
      data: {
        application: {
          company: {
            registered_company_name: selectedCompany.name,
            company_number: selectedCompany.number,
            type: selectedCompany.company_type,
            trading_from_date: selectedCompany.registration_date,
          },
          people: [
            {
              first_name: selectedPerson.first_name,
              last_name: selectedPerson.last_name,
              uid: v4(),
              roles: ['applicant', 'director', 'guarantor'],
              date_of_birth: denormaliseDate(
                values.dobDay,
                values.dobMonth,
                values.dobYear,
              ),
            },
          ],
        },
        ui: { tip_latest_signup_step: 'new' },
      },
    };

    if (searchParams.get('__impersonate') && introducerId) {
      state.data.impersonation = { introducer_id: introducerId };
    }

    const application: TApplicationRequestBody = {
      data: {
        introducer: { introducer_id: introducerId },
        requested_products: [],
      },
    };

    if (ALLOWED_PRODUCTS.includes(selectedProduct)) {
      application.data.requested_products = [{ product_type: selectedProduct }];
    }
    try {
      setIsSubmitting(true);
      const response = await fetchPostState({ body: state });
      const stateKey = response.data?.state_key;

      if (stateKey && introducerId) {
        const newApplication =
          await fetchPostApiIntroducerPortalIntroducerCustomerApplication({
            introducerId,
            customerId: stateKey,
            body: application,
          });

        trackEvent(AMPLITUDE_EVENTS.initialApplicationCheckSubmitted, {
          'State key': stateKey,
        });

        navigate(
          PATH_NAMES.newApplicationStep1({
            applicationId: newApplication.data.application_id,
          }),
        );
      }
    } catch (e) {
      datadogLogs.logger.error('Failed to create state', {
        createError: e as string,
        introducerID: introducerId,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onError = (error: any) => {
    trackEvent(AMPLITUDE_EVENTS.initialApplicationCheckErrored, {
      'Error message': error as string,
    });

    datadogLogs.logger.error('Failed to create application', {
      createError: error as string,
    });
  };

  return (
    <form
      className={styles.container}
      onSubmit={handleSubmit(onSubmit, onError)}
    >
      <Card className={styles.formContainer}>
        <div className={styles.formField}>
          <CompanyInputUK
            onChange={setCompanyWithTracking}
            setSelectedCompany={setSelectedCompany}
            setSelectedPerson={setSelectedPersonWithDOB}
          />
          {Boolean(selectedCompany) && !passesTypeCheck() && (
            <div className={styles.criteria}>
              <CriteriaItem testId="company-type">
                Unfortunately, we don't accept applications for this company
                type.
              </CriteriaItem>
            </div>
          )}
        </div>
        <div className={styles.formField}>
          <DirectorSearch
            label="Applicant's name"
            onChange={setSelectedPersonWithDOB}
            companyNumber={selectedCompany?.number}
          />
          <div className={styles.criteria}>
            <CriteriaItem
              disabled={!selectedPerson}
              hasPassed={passesDirectorCheck()}
              testId="director"
            >
              The main applicant must be a director of the company and prepared
              to provide a personal guarantee to support a loan.
            </CriteriaItem>
          </div>
        </div>
        <div className={styles.formField}>
          <div className={styles.labelWithTooltip}>
            <Label>Date of birth</Label>
            <Tooltip content="We'll use date of birth to see if we already have this applicant's details." />
          </div>
          <div className={styles.dobContainer}>
            <Input
              {...register('dobDay')}
              placeholder="DD"
              data-testid="dob-day"
              onChange={handleDOBTracking}
              error={errors.dobDay?.message}
              disabled={!selectedPerson}
            />
            <Input
              {...register('dobMonth')}
              placeholder="MM"
              data-testid="dob-month"
              onChange={handleDOBTracking}
              error={errors.dobMonth?.message}
              disabled={!selectedPerson}
            />
            <Input
              {...register('dobYear')}
              placeholder="YYYY"
              data-testid="dob-year"
              onChange={handleDOBTracking}
              error={errors.dobYear?.message}
              disabled={!selectedPerson}
            />
          </div>
        </div>
      </Card>
      <div className={styles.footerItems}>
        <CheckBox
          {...register('privacyPolicyAgreement')}
          label="I’ve got permission from the applicant to apply on their behalf"
          className={styles.privacyPolicyCheckbox}
        />
        {errors.privacyPolicyAgreement?.message && (
          <Message
            message={errors.privacyPolicyAgreement?.message}
            type="error"
          />
        )}
        <Button
          disabled={isSubmitDisabled}
          loading={isSubmitting}
          type="submit"
          variant="primary"
          size="large"
        >
          Continue
        </Button>
      </div>
    </form>
  );
};
